  // src/components/PeoplePlan/EmployeeManagement/EmployeeTableGrid.js
  import React, { useState, useEffect } from "react";
  import styles from "./GenericEmployeeTable.module.css";
  import requestSvg from "../assets/request.svg";
  //import EmployeeStatusIndicator from "./EmployeeStatusIndicator";

  const EmployeeTableGrid = ({
    employees,
    storeData,
    roleConfig,
    selectedEmployees,
    selectedEmptyPositions = [],
    onEmployeeSelect,
    onEmptyPositionSelect,
    onEmployeeClick,
    getWorkflowIcon,
    getWorkflowClass,
    enableSorting = false,
  }) => {
    // Initialize with tenure-desc as default for sortable roles
    const [sortConfig, setSortConfig] = useState({});
    const [sortedEmployees, setSortedEmployees] = useState(employees);

    // Helper function to compare tenure
    const compareTenure = (a, b, direction = "desc") => {
      const aTotalMonths = (a.tenure?.years || 0) * 12 + (a.tenure?.months || 0);
      const bTotalMonths = (b.tenure?.years || 0) * 12 + (b.tenure?.months || 0);
      return direction === "desc"
        ? bTotalMonths - aTotalMonths
        : aTotalMonths - bTotalMonths;
    };

    // Helper function to compare alphabetically
    const compareAlphabetically = (a, b, direction = "asc") => {
      const nameA = a.employee_name?.toLowerCase() || "";
      const nameB = b.employee_name?.toLowerCase() || "";
      if (nameA < nameB) return direction === "asc" ? -1 : 1;
      if (nameA > nameB) return direction === "asc" ? 1 : -1;
      return 0;
    };

    // Initialize default sort configuration
    useEffect(() => {
      if (enableSorting && roleConfig.sortable && storeData.length > 0) {
        const initialConfig = {};
        storeData.forEach((store) => {
          initialConfig[store.corp] = {
            type: "tenure",
            direction: "desc",
          };
        });
        setSortConfig(initialConfig);
      }
    }, [enableSorting, roleConfig.sortable, storeData]);

    // Apply sorting whenever employees or sortConfig changes
    useEffect(() => {
      const sorted = employees.map((store) => {
        const storeSort = sortConfig[store.storeName];

        if (!storeSort || !enableSorting || !roleConfig.sortable) {
          return store;
        }

        const sortedStoreEmployees = [...store.employees].sort((a, b) => {
          if (storeSort.type === "tenure") {
            return compareTenure(a, b, storeSort.direction);
          } else if (storeSort.type === "alphabetically") {
            return compareAlphabetically(a, b, storeSort.direction);
          }
          return 0;
        });

        return {
          ...store,
          employees: sortedStoreEmployees,
        };
      });

      setSortedEmployees(sorted);
    }, [employees, sortConfig, enableSorting, roleConfig.sortable]);

    const handleSort = (storeName, sortType) => {
      setSortConfig((prev) => {
        const currentConfig = prev[storeName] || {
          type: "tenure",
          direction: "desc",
        };

        // Toggle direction if clicking the same sort type
        if (currentConfig.type === sortType) {
          return {
            ...prev,
            [storeName]: {
              type: sortType,
              direction: currentConfig.direction === "asc" ? "desc" : "asc",
            },
          };
        }

        // Set new sort type with default direction
        return {
          ...prev,
          [storeName]: {
            type: sortType,
            direction: sortType === "tenure" ? "desc" : "asc",
          },
        };
      });
    };

    // Get sort button label with direction indicator
    const getSortButtonLabel = (storeName, sortType) => {
      const config = sortConfig[storeName];
      if (!config || config.type !== sortType) {
        return sortType === "alphabetically" ? "Sort A-Z" : "Sort by Tenure";
      }

      if (sortType === "alphabetically") {
        return config.direction === "asc" ? "Sort A-Z ↑" : "Sort Z-A ↓";
      } else {
        return config.direction === "desc" ? "Tenure ↓" : "Tenure ↑";
      }
    };

    // Get active sort class for styling
    const getSortButtonClass = (storeName, sortType) => {
      const config = sortConfig[storeName];
      const isActive = config && config.type === sortType;
      return `${styles.sortButton} ${isActive ? styles.sortButtonActive : ""}`;
    };

    // Find max rows needed - consider ALL employees across stores
    const maxRows = Math.max(
      ...storeData.map((store) => {
        const storeEmployees =
          sortedEmployees.find((s) => s.storeName === store.corp)?.employees ||
          [];
        return storeEmployees.length;
      }),
      1 // At least 1 row
    );

    // Use maxRows directly - no extra row needed
    // Only show "POSITION AVAILABLE" where other stores have employees in that row
    const displayRows = maxRows;

    return (
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              {storeData.map((store) => (
                <th key={store.corp}>
                  <div className={styles.storeHeader}>
                    <span className={styles.storeName}>{store.corp}</span>
                    {enableSorting && roleConfig.sortable && (
                      <div className={styles.sortButtons}>
                        <button
                          onClick={() => handleSort(store.corp, "alphabetically")}
                          className={getSortButtonClass(
                            store.corp,
                            "alphabetically"
                          )}
                          title="Sort by name"
                        >
                          {getSortButtonLabel(store.corp, "alphabetically")}
                        </button>
                        <button
                          onClick={() => handleSort(store.corp, "tenure")}
                          className={getSortButtonClass(store.corp, "tenure")}
                          title="Sort by tenure"
                        >
                          {getSortButtonLabel(store.corp, "tenure")}
                        </button>
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: displayRows }).map((_, rowIndex) => (
              <tr key={rowIndex}>
                {storeData.map((store) => {
                  const storeEmployees =
                    sortedEmployees.find((s) => s.storeName === store.corp)
                      ?.employees || [];
                  const employee = storeEmployees[rowIndex];

                  // Only show empty position if this store doesn't have an employee
                  // but other stores have employees in this row (for fixed positions like RM, AM, SL)
                  if (!employee) {
                    const emptyPositionKey = `${store.corp}-${roleConfig.key}-${rowIndex}`;

                    // Check if this position has an active request
                    const storeEmployeeData = sortedEmployees.find(
                      (s) => s.storeName === store.corp
                    );
                    const hasPositionRequest =
                      storeEmployeeData?.positionRequests?.some(
                        (pr) => pr.position_index === rowIndex
                      ) || false;

                    const isSelected =
                      selectedEmptyPositions?.includes(emptyPositionKey);

                    return (
                      <td key={store.corp}>
                        <div
                          className={`${styles.employeeCell} ${
                            styles.emptyPosition
                          } ${
                            hasPositionRequest ? styles.hasPositionRequest : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected || false}
                            disabled={hasPositionRequest}
                            onChange={() =>
                              onEmptyPositionSelect?.(
                                emptyPositionKey,
                                store.corp,
                                roleConfig.key
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className={styles.emptyPositionInfo}>
                            <span className={styles.vacantLabel}>
                              {hasPositionRequest
                                ? "Position Requested"
                                : "Position Available"}
                            </span>
                            <span className={styles.positionType}>
                              {roleConfig.singularName}
                            </span>
                            {hasPositionRequest && (
                              <div className={styles.workflowBadge}>
                                <img
                                  src={requestSvg}
                                  alt="Position Requested"
                                  className={styles.workflowIcon}
                                />
                                <div className={styles.tooltip}>
                                  Position Request In Progress
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  }

                  // Existing employee rendering logic
                  const hasWorkflow =
                    employee.workflow_name &&
                    employee.workflow_name !== "" &&
                    employee.workflow_name !== "No Workflow" &&
                    employee.workflow_name !== "null" &&
                    employee.workflow_name !== "undefined";

                  const hasActiveWorkflow =
                    hasWorkflow && employee.workflow_status !== "Completed";
                  const workflowClass = hasActiveWorkflow
                    ? getWorkflowClass(employee.workflow_name)
                    : "";
                  const workflowIcon = hasActiveWorkflow
                    ? getWorkflowIcon(employee.workflow_name)
                    : null;
                  const isChecked = selectedEmployees.includes(employee.user_id);

                  return (
                    <td key={store.corp}>
                      <div
                        className={`${styles.employeeCell} ${workflowClass} ${
                          hasActiveWorkflow ? styles.hasActiveWorkflow : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={hasActiveWorkflow}
                          onChange={() =>
                            onEmployeeSelect(employee.user_id, hasActiveWorkflow)
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div
                          className={styles.employeeInfo}
                          onClick={() => onEmployeeClick(employee.user_id)}
                        >
                          <div className={styles.employeeName}>
                            <span className={styles.nameText}>
                              {employee.employee_name}
                            </span>
                            {hasActiveWorkflow && (
                              <div className={styles.workflowBadge}>
                                {workflowIcon && (
                                  <img
                                    src={workflowIcon}
                                    alt={employee.workflow_name}
                                    className={styles.workflowIcon}
                                  />
                                )}
                                <div className={styles.tooltip}>
                                  {employee.workflow_name}
                                </div>
                              </div>
                            )}
                            {employee.is_minor && (
                              <div className={styles.minorBadge}>
                                <span className={styles.minorIcon}>👤</span>
                                <div className={styles.tooltip}>Is Minor</div>
                              </div>
                            )}
                          </div>
                          <div className={styles.employeeTenure}>
                            {employee.tenure?.years || 0} years{" "}
                            {employee.tenure?.months || 0} months
                          </div>
                          {/*
<EmployeeStatusIndicator
  userId={employee.user_id}
  initialStatus={employee.status_indicator}
  onStatusChange={(userId, newStatus) => {
    console.log(
      `Status updated for ${userId}: ${newStatus}`
    );
  }}
/>
*/}

                          {hasWorkflow &&
                            employee.workflow_status &&
                            employee.workflow_status !== "Completed" && (
                              <div className={styles.workflowStatus}>
                                <span
                                  style={{
                                    fontWeight: "bold",
                                    fontStyle: "normal",
                                  }}
                                >
                                  Workflow Status:
                                </span>{" "}
                                {employee.workflow_status}
                              </div>
                            )}
                        </div>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  export default EmployeeTableGrid;
